import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbVBox } from "abreact/layout/AbVBox";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbBigDecimal } from "abweb/utils/AbBigDecimal";
import { AbDecimal } from "abweb/utils/AbDecimal";
import { NumberUtils } from "abweb/utils/NumberUtils";
import * as React from "react";

/**
 * The properties for an **AbDecimalInput**.
 */

const getCharRegex = () => {
  const s = NumberUtils.getLocaleDecimalSeparator();
  const pattern = `[-+\\deE\\${s}]`;
  return new RegExp(pattern);
};
const getErrorString = (isValid, inRange, min, max) => {
  if (!isValid) return AbReactResources.decimalInput_invalidDecimal_format();
  let returnString = AbReactResources.decimalInput_invalidDecimal_range();
  if (min !== undefined) returnString += `\n${AbReactResources.invalidValue_greaterThan(min.toString())}`;
  if (max !== undefined) returnString += `\n${AbReactResources.invalidValue_lessThan(max.toString())}`;
  return returnString;
};
const getTooltip = (tooltip, precision) => {
  if (precision === undefined) return tooltip;
  const precisionString = AbReactResources.decimalInput_precision(precision.toString());
  if (!tooltip) return precisionString;
  return /*#__PURE__*/React.createElement(AbVBox, null, tooltip, /*#__PURE__*/React.createElement(AbHSeparator, null), precisionString);
};

/**
 * The **AbDecimalInput** component allows the user to enter a decimal.
 */
export function AbDecimalInput(props) {
  const contextSize = React.useContext(AbSizeContext);
  const {
    id,
    className,
    style,
    ...inputProps
  } = ReactUtils.inputProps("ab-decimal-input", props, ["abui", "ab-single-line", props.size ?? contextSize ?? AbSize.MEDIUM, "ab-font-feature-tabular-numbers"]);
  // Manually remove data-test-id because deconstruction syntax can't.
  delete inputProps["data-test-id"];
  const decimalPlaces = props.decimalPlaces ?? props.precision;
  const isInRange = React.useCallback(n => {
    const min = props.min !== undefined ? AbBigDecimal.from(props.min) : n;
    const max = props.max !== undefined ? AbBigDecimal.from(props.max) : n;
    return min.compare(n) !== 1 && max.compare(n) !== -1;
  }, [props.min, props.max]);
  const getBigDecimal = () => {
    if (props.value === undefined) return undefined;
    const bigDecimal = AbBigDecimal.from(props.value.serialize());
    if (bigDecimal.isNaN()) return undefined;
    return bigDecimal;
  };
  const {
    textIsValid,
    inRange,
    inputString,
    textOnChange,
    onCommit
  } = AbHooks.useStringValidation({
    value: getBigDecimal(),
    onChange: bigDecimal => props.onChange(bigDecimal === undefined ? undefined : new AbDecimal(bigDecimal.toString())),
    inRange: isInRange,
    format: {
      format: value => value.toLocaleString(),
      parse: s => {
        const value = AbBigDecimal.from(s);
        if (value.isNaN()) return new Error(`'s' is not a valid number string.`);
        if (decimalPlaces === undefined) return value;
        return AbBigDecimal.truncate(value, decimalPlaces);
      }
    },
    equals: AbBigDecimal.equals
  });
  const partialRef = React.useRef({});
  const isWhole = partial => !!partial.inputElement && !!partial.clear;
  const textInputRef = AbHooks.useComposeRef(props, partialRef.current, "inputElement", isWhole);
  partialRef.current.clear = () => textOnChange("");
  const inner = /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: getTooltip(props.tooltip, decimalPlaces)
  }, /*#__PURE__*/React.createElement("div", {
    style: style,
    className: className,
    id: id,
    "data-test-id": props.dataTestId
  }, /*#__PURE__*/React.createElement(AbTextInput, _extends({}, inputProps, {
    className: ReactUtils.classNamesString([(!textIsValid || !inRange) && "ab-input-error", "ab-default-input-width"]),
    value: inputString,
    placeholder: props.placeholder,
    onChange: v => {
      textOnChange(v);
    },
    onFocus: props.onFocus,
    onEnter: () => {
      onCommit(inputString);
      props.onEnter?.();
    },
    onBlur: () => {
      props.onBlur?.();
      onCommit(inputString, true);
    },
    onDragOver: props.onDragOver,
    abRef: element => textInputRef(element?.inputElement ?? null),
    __htmlProps: {
      onKeyDown: e => {
        if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
        if (!getCharRegex().test(e.key)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    }
  })), (!textIsValid || !inRange) && /*#__PURE__*/React.createElement(AbIcon, {
    image: "absdk:error",
    className: "ab-input-error-icon",
    tooltip: getErrorString(textIsValid, inRange, props.min, props.max)
  })));
  return sizeProviderWrapper(inner, props, contextSize);
}
//# sourceMappingURL=AbDecimalInput.js.map
